import React, { useMemo, useState } from 'react';
import { Typography, Box, Tooltip, IconButton } from '@mui/material';
import { ThumbsUp, ThumbsDown } from 'lucide-mui';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FileType } from '../../../types';
import { apiV2 } from '../../../lib/baseAxios';
import { RootState } from '../../../reducers';
import FileList from './FileList';

// Extend FileType for assistant search results
export type AssistantFileType = FileType & {
  fileId?: string;
  fileName?: string;
};

interface SearchResultsProps {
  results: {
    data: AssistantFileType[];
    error: string | null;
  };
  isFileListExpanded: boolean;
  toggleFileList: () => void;
  fileThumbnails: Array<{
    fileId: string;
    thumbnailUrl: string | null;
    isLoading: boolean;
  }>;
  setFileThumbnails: React.Dispatch<
    React.SetStateAction<
      Array<{
        fileId: string;
        thumbnailUrl: string | null;
        isLoading: boolean;
      }>
    >
  >;
  handleClose: () => void;
  isLoading?: boolean;
  query?: string;
  aiSummary?: string;
}

/**
 * SearchResults component displays the search results, including a file list
 * and handles related functionality
 */
const SearchResults: React.FC<SearchResultsProps> = ({
  results,
  isFileListExpanded,
  toggleFileList,
  fileThumbnails,
  setFileThumbnails,
  isLoading = false,
  query = '',
  aiSummary = '',
}) => {
  const { t } = useTranslation();
  // Get user settings from Redux state
  const userSettings = useSelector((state: RootState) => state.settings.user);

  // State for feedback (thumbs up/down)
  const [feedbackState, setFeedbackState] = useState<'up' | 'down' | null>(
    null
  );
  // Always show all search results, regardless of citations
  const citedFiles = useMemo(() => {
    // Deduplicate results by both file ID and file name to ensure no duplicates
    const seenIds = new Set<string>();
    const seenNames = new Set<string>();
    const uniqueResults = results.data
      .filter((file) => {
        const id = file.fileId || file._id;
        const name = file.fileName || file.name;

        // Skip if no valid ID or name
        if (!id || !name) {
          return false;
        }

        // Skip if we've already seen this ID or name
        if (seenIds.has(id) || seenNames.has(name)) {
          return false;
        }

        seenIds.add(id);
        seenNames.add(name);
        return true;
      })
      .map((file) => ({
        ...file,
        fileId: file.fileId || file._id,
        fileName: file.fileName || file.name,
      }));

    return uniqueResults;
  }, [results.data]);

  const getFileIds = () =>
    results.data
      .map((file) => file.fileId || file._id)
      .filter(Boolean)
      .slice(0, 10);

  const handleFeedback = async (value: 1 | -1) => {
    try {
      const fileIds = getFileIds();
      await apiV2.post('/feedbackcounts', {
        feature: 'search',
        value,
        userId: userSettings._id,
        metadata: {
          query,
          results: {
            count: results.data.length,
            fileIds,
          },
          summaryText: aiSummary,
        },
      });
      console.log(
        `Feedback submitted: ${value === 1 ? 'thumbs up' : 'thumbs down'}`
      );
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
  };

  const deleteFeedback = async () => {
    try {
      const fileIds = getFileIds();
      await apiV2.delete('/feedbackcounts', {
        data: {
          feature: 'search',
          userId: userSettings._id,
          query,
          fileIds,
        },
      });
      setFeedbackState(null);
      console.log('Feedback deleted');
    } catch (error) {
      console.error('Error deleting feedback:', error);
    }
  };

  const handleThumbsUpClick = () => {
    if (feedbackState === 'up') {
      // If already thumbs up, remove feedback
      deleteFeedback();
    } else {
      // Set thumbs up and remove any thumbs down
      setFeedbackState('up');
      handleFeedback(1);
    }
  };

  const handleThumbsDownClick = () => {
    if (feedbackState === 'down') {
      // If already thumbs down, remove feedback
      deleteFeedback();
    } else {
      // Set thumbs down and remove any thumbs up
      setFeedbackState('down');
      handleFeedback(-1);
    }
  };
  return (
    <Box sx={{ position: 'relative' }}>
      {/* File List Section with Toggle */}
      {citedFiles.length > 0 && (
        <FileList
          files={citedFiles}
          isExpanded={isFileListExpanded}
          onToggleExpand={toggleFileList}
          fileThumbnails={fileThumbnails}
          setFileThumbnails={setFileThumbnails}
          isLoading={isLoading}
          label={t('assistant.search.results', { count: citedFiles.length })}
        />
      )}

      {/* Display message when no results found */}
      {citedFiles.length === 0 && (
        <Typography variant="subtitle2" color="text.secondary" sx={{ p: 1 }}>
          {t('assistant.search.noResults')}
        </Typography>
      )}

      {/* Feedback buttons: thumbs up/down */}
      {results.data.length > 0 && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 8,
            right: 8,
            display: 'flex',
            gap: 1,
            zIndex: 2,
          }}
        >
          <Tooltip title={t('assistant.search.feedback.helpful')}>
            <IconButton
              size="small"
              color={feedbackState === 'up' ? 'success' : 'default'}
              onClick={handleThumbsUpClick}
              aria-label={t('assistant.search.feedback.helpful')}
              sx={{
                color:
                  feedbackState === 'up' ? 'success.main' : 'text.secondary',
                '&:hover': {
                  color:
                    feedbackState === 'up' ? 'success.dark' : 'success.main',
                },
              }}
            >
              <ThumbsUp
                width={20}
                height={20}
                style={{
                  fill: feedbackState === 'up' ? 'currentColor' : 'none',
                  stroke: 'currentColor',
                }}
              />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('assistant.search.feedback.notHelpful')}>
            <IconButton
              size="small"
              color={feedbackState === 'down' ? 'error' : 'default'}
              onClick={handleThumbsDownClick}
              aria-label={t('assistant.search.feedback.notHelpful')}
              sx={{
                color:
                  feedbackState === 'down' ? 'error.main' : 'text.secondary',
                '&:hover': {
                  color: feedbackState === 'down' ? 'error.dark' : 'error.main',
                },
              }}
            >
              <ThumbsDown
                width={20}
                height={20}
                style={{
                  fill: feedbackState === 'down' ? 'currentColor' : 'none',
                  stroke: 'currentColor',
                }}
              />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};

export default SearchResults;
